/* eslint-disable no-underscore-dangle */
import React, { useState, useContext, useEffect } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as Sentry from '@sentry/browser';

import { Box } from 'grommet';

import { FullScreenSkeleton } from '../../helpers/Loaders';
import { UserContext } from './context';
import { AppHostContext } from '@services/appHost';
import axios from '../../helpers/axios';
import { theFirebase } from '../Firebase/firebase';
import { AugmentedUser } from './store';
import { strogging } from '@shd/jslib/infra';
import { docStorage } from '@shd/jslib/models';
import { analytics } from '../analytics';
import { Purchases } from '@revenuecat/purchases-js';
import { getRevenueCatUserId } from '../../helpers/utils';

import AccountInfo from '../../features/SignIn/AccountInfo';
import * as ROUTES from '@constants/routes';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import SHDModal from '@ui/Modal/SHDModal';

const withAuthentication = (
  Component: React.ComponentType<RouteComponentProps>
) =>
  withRouter((props: RouteComponentProps) => {
    const userStore = useContext(UserContext);
    const appHost = useContext(AppHostContext);

    const [loading, toggleLoading] = useState(true);
    const [tsTosAccept, setTsTosAccept] = useState<number>();

    const setRedirect = () => {
      const teams = userStore.userTeams;
      const players = userStore.userPlayers;
      if (teams.length > 0 || players.length > 0) {
        return { fromInvite: true, redirectUrl: ROUTES.HOME };
      } else {
        return { fromInvite: false, redirectUrl: ROUTES.ONBOARDING };
      }
    };

    useEffect(() => {
      Sentry.setUser({
        id: userStore.authUser?.claims?.shd_user_id as string | undefined,
      });
      if (userStore.authUser) {
        const shdUserId = userStore.authUser?.claims?.shd_user_id as string;
        if (shdUserId) {
          //theFirebase().setAnalyticsUserId(shdUserId);
          // theFirebase().setAnalyticsUserProperties({ shd_user_id: shdUserId });
          analytics.identify(shdUserId);
        }
        userStore.setHelpWidgetAuth();
        axios
          .get('/api/user_tos')
          .then((response) => {
            if (response.data !== false) {
              setTsTosAccept(response.data);
            }
          })
          .catch((e) => {
            strogging.exception('Failed to get user tos', e);
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userStore.authUser]);

    useEffect(
      () =>
        theFirebase().auth.onIdTokenChanged(async (user) => {
          if (user) {
            const result = await user.getIdTokenResult();
            const modifiedUser: AugmentedUser = user;
            modifiedUser.claims = result.claims;

            const isStopImpersonation =
              props.location.pathname.includes('stop_impersonation');

            modifiedUser.spoofedUser = null;
            if (
              'is_admin' in result.claims &&
              result.claims.is_admin &&
              'spoof_user_id' in result.claims &&
              result.claims.spoof_user_id !== null &&
              result.claims.spoof_user_id !== '' &&
              !isStopImpersonation
            ) {
              try {
                const response = await axios.get<docStorage.UserDoc>(
                  `/api/user/${result.claims.spoof_user_id}`
                );
                const spoofedUser = response.data;
                modifiedUser.spoofedUser = spoofedUser;
              } catch (e) {
                Sentry.captureException(e);
              }
            }

            if (Math.random() < 0.2) {
              // some of the time, track identity with sendinblue
              if (modifiedUser && modifiedUser.email) {
                window.sendinblue.identify(modifiedUser.email);
              }
            }

            await Purchases.configure(
              import.meta.env.VITE_APP_REVENUECAT_BILLING_KEY || '',
              getRevenueCatUserId(modifiedUser.claims.shd_user_id)
            );

            // update the environment for the user
            // ok if this fails
            axios.post(
              `/api/revenuecat/customers/${getRevenueCatUserId(
                modifiedUser.claims.shd_user_id
              )}/environment`,
              { environment: import.meta.env.MODE }
            );
            userStore.setAuthUser(modifiedUser);
            toggleLoading(false);
            userStore
              .apiSetTeams()
              .then(() => {
                toggleLoading(false);
              })
              .catch((e) => {
                Sentry.captureException(e);
                toggleLoading(false);
              });
          } else {
            userStore.setAuthUser(null);
            toggleLoading(false);
          }
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );
    if (loading) {
      return <FullScreenSkeleton />;
    }
    const shouldShowAccountInfoModal = () => {
      const isUserMissingInfo =
        !userStore.user.nameFirst ||
        userStore.user.nameFirst === 'Unknown' ||
        !userStore.user.nameLast ||
        userStore.user.nameLast === 'Signin' ||
        !userStore.user.email ||
        !userStore.user.phoneList;

      const isAdminSpoofing =
        userStore.authUser &&
        userStore.authUser.claims &&
        'claims' in userStore.authUser &&
        'spoof_user_id' in userStore.authUser.claims &&
        userStore.authUser.claims.spoof_user_id &&
        userStore.authUser.claims.is_admin;

      return (
        (appHost.isHosted || isUserMissingInfo) &&
        tsTosAccept === 0 &&
        !isAdminSpoofing
      );
    };

    return (
      <>
        <Component {...props} />
        {shouldShowAccountInfoModal() && (
          <SHDThemeOverride>
            <SHDModal>
              <Box gap="large" margin="xsmall">
                <AccountInfo
                  onCollectionComplete={() => setTsTosAccept(1)}
                  {...setRedirect()}
                />
              </Box>
            </SHDModal>
          </SHDThemeOverride>
        )}
      </>
    );
  });

export default withAuthentication;
